import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  apiClient,
  buildQueryString,
  withMinDelay,
  withMinDelayThunk,
} from 'utils/api';
import { NOTIFICATION_DROPDOWN_LIMIT, NOTIFICATION_READ_FILTERS } from 'constants/notifications';
import { serializeNotificationReadFilter, sortNotificationsForPreview } from 'utils/notifications';

const DEFAULT_NOTIFICATION_FILTERS = {
  search: '',
  type: 'ALL',
  channel: 'ALL',
  readState: NOTIFICATION_READ_FILTERS.ALL,
};

const replaceNotificationById = (items, notification) => items.map((item) => (
  item._id === notification._id ? { ...item, ...notification } : item
));

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { initialFetchDone } = getState().notifications;
      const queryString = buildQueryString(params);
      const url = queryString ? `/notifications?${queryString}` : '/notifications';
      const response = !initialFetchDone
        ? await withMinDelay(apiClient.get(url))
        : await apiClient.get(url);

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notifications');
    }
  }
);

export const fetchNotificationPreview = createAsyncThunk(
  'notifications/fetchNotificationPreview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/notifications?page=1&limit=${NOTIFICATION_DROPDOWN_LIMIT}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch notification preview');
    }
  }
);

export const fetchUnreadNotificationCount = createAsyncThunk(
  'notifications/fetchUnreadNotificationCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch unread notification count');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markNotificationRead',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/notifications/${id}/read`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as read');
    }
  }
);

export const markNotificationUnread = createAsyncThunk(
  'notifications/markNotificationUnread',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/notifications/${id}/unread`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notification as unread');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch('/notifications/read');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark notifications as read');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  withMinDelayThunk(async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete notification');
    }
  })
);

const initialState = {
  items: [],
  previewItems: [],
  pageInfo: null,
  filters: DEFAULT_NOTIFICATION_FILTERS,
  searchFilterValue: DEFAULT_NOTIFICATION_FILTERS.search,
  typeFilterValue: DEFAULT_NOTIFICATION_FILTERS.type,
  channelFilterValue: DEFAULT_NOTIFICATION_FILTERS.channel,
  readStateFilterValue: DEFAULT_NOTIFICATION_FILTERS.readState,
  unreadCount: 0,
  unreadCountStatus: 'idle',
  loading: false,
  previewLoading: false,
  error: null,
  previewError: null,
  status: 'idle',
  initialFetchDone: false,
  previewInitialFetchDone: false,
  pendingDeletesById: {},
  pendingReadsById: {},
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      const nextFilters = {
        ...DEFAULT_NOTIFICATION_FILTERS,
        ...(state.filters || {}),
        ...action.payload,
      };

      state.filters = nextFilters;
      state.searchFilterValue = nextFilters.search;
      state.typeFilterValue = nextFilters.type;
      state.channelFilterValue = nextFilters.channel;
      state.readStateFilterValue = nextFilters.readState;
    },
    clearError: (state) => {
      state.error = null;
      state.previewError = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
      state.unreadCountStatus = 'idle';
    },
    clearNotificationPreview: (state) => {
      state.previewItems = [];
      state.previewError = null;
      state.previewInitialFetchDone = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.pageInfo = action.payload.pageInfo || null;
        state.initialFetchDone = true;
        state.status = 'succeeded';
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })

      .addCase(fetchNotificationPreview.pending, (state) => {
        state.previewLoading = true;
        state.previewError = null;
      })
      .addCase(fetchNotificationPreview.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.previewItems = sortNotificationsForPreview(action.payload.data || []);
        state.previewInitialFetchDone = true;
      })
      .addCase(fetchNotificationPreview.rejected, (state, action) => {
        state.previewLoading = false;
        state.previewError = action.payload;
      })

      .addCase(fetchUnreadNotificationCount.pending, (state) => {
        state.unreadCountStatus = 'loading';
      })
      .addCase(fetchUnreadNotificationCount.fulfilled, (state, action) => {
        state.unreadCount = Number(action.payload?.data?.count) || 0;
        state.unreadCountStatus = 'succeeded';
      })
      .addCase(fetchUnreadNotificationCount.rejected, (state, action) => {
        state.unreadCountStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(markNotificationRead.pending, (state, action) => {
        state.pendingReadsById[action.meta.arg] = true;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = action.payload?.data;
        const existingNotification = state.items.find((item) => item._id === notification?._id)
          || state.previewItems.find((item) => item._id === notification?._id);
        const wasUnread = existingNotification ? !existingNotification.isRead : false;

        delete state.pendingReadsById[action.meta.arg];

        if (notification?._id) {
          state.items = replaceNotificationById(state.items, notification);
          state.previewItems = replaceNotificationById(state.previewItems, notification);
        }

        if (wasUnread && notification?.isRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        delete state.pendingReadsById[action.meta.arg];
        state.error = action.payload;
      })

      .addCase(markNotificationUnread.pending, (state, action) => {
        state.pendingReadsById[action.meta.arg] = true;
      })
      .addCase(markNotificationUnread.fulfilled, (state, action) => {
        const notification = action.payload?.data;
        const existingNotification = state.items.find((item) => item._id === notification?._id)
          || state.previewItems.find((item) => item._id === notification?._id);
        const wasRead = existingNotification ? Boolean(existingNotification.isRead) : false;

        delete state.pendingReadsById[action.meta.arg];

        if (notification?._id) {
          state.items = replaceNotificationById(state.items, notification);
          state.previewItems = replaceNotificationById(state.previewItems, notification);
        }

        if (wasRead && notification && !notification.isRead) {
          state.unreadCount += 1;
        }
      })
      .addCase(markNotificationUnread.rejected, (state, action) => {
        delete state.pendingReadsById[action.meta.arg];
        state.error = action.payload;
      })

      .addCase(markAllNotificationsRead.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items = state.items.map((item) => ({ ...item, isRead: true }));
        state.previewItems = state.previewItems.map((item) => ({ ...item, isRead: true }));
        state.unreadCount = 0;
        state.status = 'succeeded';
      })
      .addCase(markAllNotificationsRead.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(deleteNotification.pending, (state, action) => {
        state.pendingDeletesById[action.meta.arg] = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        delete state.pendingDeletesById[action.payload];
        delete state.pendingReadsById[action.payload];

        const removedPreviewItem = state.previewItems.find((item) => item._id === action.payload);
        const removedListItem = state.items.find((item) => item._id === action.payload);

        state.items = state.items.filter((item) => item._id !== action.payload);
        state.previewItems = state.previewItems.filter((item) => item._id !== action.payload);

        if ((removedPreviewItem && !removedPreviewItem.isRead) || (removedListItem && !removedListItem.isRead)) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }

        state.status = 'succeeded';
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        delete state.pendingDeletesById[action.meta.arg];
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const {
  clearError,
  resetStatus,
  setFilters,
  clearNotificationPreview,
} = notificationsSlice.actions;

export const selectAllNotifications = (state) => state.notifications.items;
export const selectNotificationPreviewItems = (state) => state.notifications.previewItems;
export const selectNotificationsPageInfo = (state) => state.notifications.pageInfo;
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectNotificationPreviewLoading = (state) => state.notifications.previewLoading;
export const selectNotificationsInitialFetchDone = (state) => state.notifications.initialFetchDone;
export const selectNotificationPreviewInitialFetchDone = (state) => state.notifications.previewInitialFetchDone;
export const selectNotificationsError = (state) => state.notifications.error;
export const selectNotificationPreviewError = (state) => state.notifications.previewError;
export const selectNotificationsStatus = (state) => state.notifications.status;
export const selectUnreadNotificationCount = (state) => state.notifications.unreadCount;
export const selectUnreadNotificationCountStatus = (state) => state.notifications.unreadCountStatus;
export const selectHasUnreadNotifications = (state) => state.notifications.unreadCount > 0;
export const selectNotificationPendingDeletes = (state) => state.notifications.pendingDeletesById;
export const selectNotificationPendingReads = (state) => state.notifications.pendingReadsById;
const selectNotificationsState = (state) => state.notifications;

export const selectNotificationsFilters = createSelector(
  [selectNotificationsState],
  (notificationsState) => {
    if (notificationsState.filters) {
      return notificationsState.filters;
    }

    return {
      search: notificationsState.searchFilterValue ?? DEFAULT_NOTIFICATION_FILTERS.search,
      type: notificationsState.typeFilterValue ?? DEFAULT_NOTIFICATION_FILTERS.type,
      channel: notificationsState.channelFilterValue ?? DEFAULT_NOTIFICATION_FILTERS.channel,
      readState: notificationsState.readStateFilterValue ?? DEFAULT_NOTIFICATION_FILTERS.readState,
    };
  }
);

export const buildNotificationFetchParams = (filters, page, limit) => {
  const params = {
    page,
    limit,
  };

  if (filters.search?.trim()) {
    params.search = filters.search.trim();
  }

  if (filters.type && filters.type !== 'ALL') {
    params.type = filters.type;
  }

  if (filters.channel && filters.channel !== 'ALL') {
    params.channel = filters.channel;
  }

  const isRead = serializeNotificationReadFilter(filters.readState);

  if (isRead !== undefined) {
    params.isRead = isRead;
  }

  return params;
};

export default notificationsSlice.reducer;
